import { EventQuery, PushQuery } from 'queries';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CSSObject } from '@emotion/react';
import { FormComponent, UiComponent, UserGroupComponent } from 'components';
import { ContentSelect, EventSelect } from 'components/push';
import {
  DESTINATIONS_WITH_DETAIL,
  DIRECT_PUSH_DESTINATION_OPTIONS,
  PUSH_BODY_MAX_LENGTH,
  PUSH_TITLE_MAX_LENGTH,
} from 'components/push/constants';
import {
  EventItemType,
  IContent,
  IEnroll,
  IUserGroupCandidate,
  PushDestinationType,
} from 'types';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import useModal from 'hooks/useModal';
import * as styles from 'components/push/Form/styles';
import { mobile } from 'styles/media';

const targetWrap: CSSObject = {
  width: 1000,
  maxWidth: '80vw',
  [mobile]: { width: '100%', maxWidth: 'none' },
};

const footer: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '8px',
};

const count: CSSObject = { fontSize: '13px', color: '#8a8d94' };

const contentWrap: CSSObject = {
  width: '600px',
  maxWidth: '100%',
};

const contentField: CSSObject = {
  ...styles.field,
  [mobile]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
    minHeight: 0,
  },
};

const contentLabel: CSSObject = {
  ...styles.label,
  [mobile]: {
    width: 'auto',
    lineHeight: 1.4,
  },
};

const contentControl: CSSObject = {
  minWidth: 0,
  [mobile]: {
    width: '100%',
    '> label': {
      display: 'block',
    },
  },
};

const fullWidthOnMobile = (width: number): CSSObject => ({
  width,
  [mobile]: {
    width: '100%',
  },
});

const destinationRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '> select': {
    width: 200,
  },
  [mobile]: {
    flexWrap: 'wrap',
    '> select': {
      width: '100%',
    },
  },
};

const contentFooter: CSSObject = {
  ...footer,
  [mobile]: {
    marginTop: 20,
    button: {
      height: 48,
      minWidth: 96,
    },
  },
};

const toCandidate = ({ user }: IEnroll): IUserGroupCandidate => ({
  id: user.id,
  name: user.name,
  phone: user.phone,
});

type Step = 'TARGET' | 'CONTENT';

interface Props {
  targets?: IEnroll[];
}

function NotificationModal({ targets }: Props) {
  const { handleClose, handleConfirm } = useModal();
  const { id } = useParams<{ id: string }>();
  const { mutate, isPending } = PushQuery.usePostDirect();
  const { data: event } = EventQuery.useGet(id ?? '');

  const [step, setStep] = useState<Step>(targets ? 'CONTENT' : 'TARGET');
  const [selected, setSelected] = useState<IUserGroupCandidate[]>(
    (targets ?? []).map(toCandidate),
  );

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [destinationType, setDestinationType] =
    useState<PushDestinationType>('CONTENTS');
  const [selectedContent, setSelectedContent] = useState<IContent | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventItemType | null>(
    null,
  );
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // 피드백은 이 행사 고정, 나머지는 선택한 대상
  const destinationDetail = (() => {
    if (destinationType === 'EVENT_FEEDBACK') return id;
    if (destinationType === 'EVENT_DETAIL') {
      return selectedEvent ? String(selectedEvent.id) : undefined;
    }
    if (destinationType === 'CONTENTS') {
      return selectedContent ? String(selectedContent.id) : undefined;
    }
    return undefined;
  })();

  const handleNext = () => {
    if (selected.length === 0) {
      return toast.error('받을 회원을 선택해주세요.');
    }
    setStep('CONTENT');
  };

  const handleSubmit = () => {
    if (!title.trim()) return toast.error('제목을 입력해주세요.');
    if (!body.trim()) return toast.error('내용을 입력해주세요.');
    if (selected.length === 0) {
      setStep('TARGET');
      return toast.error('받을 회원을 선택해주세요.');
    }
    if (
      DESTINATIONS_WITH_DETAIL.includes(destinationType) &&
      !destinationDetail
    ) {
      return toast.error('연결 대상을 선택해주세요.');
    }

    handleConfirm({
      confirmContent: `총 ${selected.length.toLocaleString('ko')}명에게 알림을 발송하시겠습니까?`,
      yesTitle: '발송',
      confirmNext: () =>
        mutate(
          {
            title: title.trim(),
            body: body.trim(),
            userIds: selected.map((u) => u.id),
            ...(id ? { eventId: Number(id) } : {}),
            destinationType,
            ...(destinationDetail ? { destinationDetail } : {}),
          },
          {
            onSuccess: () => {
              toast.success('알림이 전송되었습니다.');
              handleClose();
            },
            onError: () => {
              toast.error(DEFAULT_ERROR_MESSAGE);
            },
          },
        ),
    });
  };

  if (step === 'TARGET') {
    // 봉사 기준 탭이 이 봉사를 골라둔 채로 열리도록 봉사 정보를 기다린다.
    if (id && !event) return null;

    return (
      <div css={targetWrap}>
        <UserGroupComponent.Picker
          selected={selected}
          onChange={setSelected}
          sources={['ENROLL', 'GROUP', 'USER']}
          initialEvent={event}
          isEventLocked
        />
        <div css={contentFooter}>
          <p css={count}>총 {selected.length.toLocaleString('ko')}명 선택</p>
          <UiComponent.Button
            variant="contained"
            color="primary"
            disabled={selected.length === 0}
            onClick={handleNext}
          >
            다음
          </UiComponent.Button>
        </div>
      </div>
    );
  }

  return (
    <div css={contentWrap}>
      <div css={styles.fields}>
        <div css={contentField}>
          <p css={contentLabel}>제목</p>
          <div css={contentControl}>
            <FormComponent.MaxInput
              css={fullWidthOnMobile(360)}
              maxLength={PUSH_TITLE_MAX_LENGTH}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        </div>

        <div css={contentField}>
          <p css={contentLabel}>내용</p>
          <div css={contentControl}>
            <FormComponent.MaxTextarea
              css={{ ...fullWidthOnMobile(360), height: '80px' }}
              maxLength={PUSH_BODY_MAX_LENGTH}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
        </div>

        <div css={contentField}>
          <p css={contentLabel}>연결 화면</p>
          <div css={destinationRow}>
            <FormComponent.Select
              options={DIRECT_PUSH_DESTINATION_OPTIONS}
              value={destinationType}
              handleChange={(value) => {
                setDestinationType(value);
                setSelectedContent(null);
                setSelectedEvent(null);
              }}
            />
            {destinationType === 'CONTENTS' && (
              <>
                <UiComponent.Button
                  variant="border"
                  color="primary"
                  css={{ height: '34px' }}
                  onClick={() => setIsContentModalOpen(true)}
                >
                  컨텐츠 선택
                </UiComponent.Button>
                <span css={styles.selectedEvent}>
                  {selectedContent
                    ? `#${selectedContent.id} ${selectedContent.title}`
                    : '선택된 컨텐츠 없음'}
                </span>
              </>
            )}
            {destinationType === 'EVENT_DETAIL' && (
              <>
                <UiComponent.Button
                  variant="border"
                  color="primary"
                  css={{ height: '34px' }}
                  onClick={() => setIsEventModalOpen(true)}
                >
                  봉사 선택
                </UiComponent.Button>
                <span css={styles.selectedEvent}>
                  {selectedEvent
                    ? `#${selectedEvent.id} ${selectedEvent.title}`
                    : '선택된 봉사 없음'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div css={contentFooter}>
        <p css={count}>총 {selected.length}명 선택</p>
        <div css={{ display: 'flex', gap: '0 8px' }}>
          {!targets && (
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => setStep('TARGET')}
            >
              이전
            </UiComponent.Button>
          )}
          <UiComponent.Button
            color="primary"
            variant="contained"
            disabled={isPending}
            onClick={handleSubmit}
          >
            전송
          </UiComponent.Button>
        </div>
      </div>

      {isContentModalOpen && (
        <ContentSelect
          onSelect={(content) => {
            setSelectedContent(content);
            setIsContentModalOpen(false);
          }}
          onClose={() => setIsContentModalOpen(false)}
        />
      )}
      {isEventModalOpen && (
        <EventSelect
          onSelect={(event) => {
            setSelectedEvent(event);
            setIsEventModalOpen(false);
          }}
          onClose={() => setIsEventModalOpen(false)}
        />
      )}
    </div>
  );
}

export default NotificationModal;
